import React, { useState, useEffect, useRef } from 'react';
import './AIAgent.css';

const AIAgent = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [error, setError] = useState('');
  const [ragContext, setRagContext] = useState(null);
  const messagesEndRef = useRef(null);

  const providers = [
    { value: 'openai', label: 'OpenAI GPT' },
    { value: 'gemini', label: 'Google Gemini' },
    { value: 'anthropic', label: 'Anthropic Claude' },
    { value: 'local', label: 'Local Model' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');
    setRagContext(null);

    try {
      const response = await fetch('/api/ai-agent/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: inputMessage,
          provider: selectedProvider,
          conversationHistory: messages.slice(-10) // Send last 10 messages for context
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.ragContext) {
        // Do not display RAG context in chat UI
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toLocaleTimeString(),
        provider: selectedProvider
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(`Failed to send message: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
    setRagContext(null);
  };

  const formatMessageContent = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>');
  };

  const renderRAGContext = (context) => {
    if (!context || !context.length) return null;

    return (
      <div className="rag-context">
        <div className="rag-context-header">
          Relevant Well Data Found:
        </div>
        {context.map((item, index) => (
          <div key={index} className="rag-context-item">
            <strong>Score: {item.score?.toFixed(3)}</strong><br/>
            {item.content}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="ai-agent-container">
      <div className="ai-agent-header">
        <h1 className="ai-agent-title">AI Well Data Assistant</h1>
        <div className="provider-selector">
          <label htmlFor="provider-select">AI Provider:</label>
          <select
            id="provider-select"
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
          >
            {providers.map(provider => (
              <option key={provider.value} value={provider.value}>
                {provider.label}
              </option>
            ))}
          </select>
          <button 
            className="clear-chat-button"
            onClick={clearChat}
            title="Clear chat history"
          >
            Clear Chat
          </button>
        </div>
      </div>

      <div className="chat-container">
        <div className="chat-messages">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {messages.length === 0 && (
            <div className="message assistant">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <p>Hello! I'm your AI Well Data Assistant. I can help you with:</p>
                <ul>
                  <li>Finding specific well information</li>
                  <li>Analyzing well statistics and trends</li>
                  <li>Comparing wells across different fields or countries</li>
                  <li>Answering questions about drilling data</li>
                </ul>
                <p>Ask me anything about your well data!</p>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="message-avatar">
                {message.role === 'user' ? '👤' : '🤖'}
              </div>
              <div className="message-content">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: formatMessageContent(message.content) 
                  }}
                />
                <div className="message-timestamp">
                  {message.timestamp}
                  {message.provider && ` • ${providers.find(p => p.value === message.provider)?.label}`}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message assistant">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="loading-indicator">
                  <span>Thinking</span>
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <textarea
            className="chat-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about your well data..."
            disabled={isLoading}
            rows={1}
          />
          <button
            className="send-button"
            onClick={handleSendMessage}
            disabled={isLoading || !inputMessage.trim()}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAgent; 